import { PaymentPanel } from "../components/payment/PaymentPanel";
import { ErrorPanel, LoadingPanel } from "../components/StatePanels";
import { useAccountPage } from "./useAccountPage";
import { formatMoney } from "../lib/format";

type Props = {
  customerCode?: string;
};

export function AccountPage({ customerCode }: Props) {
  const page = useAccountPage(customerCode);

  if (page.status === "empty")
    return (
      <section class="empty-state">
        <h2>Find an account</h2>
        <p>Enter an account payment link or search from the payment page.</p>
      </section>
    );

  if (page.status === "loading") return <LoadingPanel title="Loading account" />;

  if (page.status === "notFound")
    return (
      <ErrorPanel
        title="Account not found"
        message={page.message}
        action={{ href: page.returnRoute, label: "Back to search" }}
      />
    );

  if (page.status === "error")
    return <ErrorPanel title="Unable to load account" message={page.message} />;

  return <AccountView page={page} />;
}

function AccountView({
  page,
}: {
  page: Extract<ReturnType<typeof useAccountPage>, { status: "ready" }>;
}) {
  const { customerCode, viewModel } = page;

  return (
    <section class="invoice-page" aria-label="Account details">
      <div class="checkout-layout">
        <div class="checkout-main">
          <section class="invoice-document account-document">
            <header class="document-header">
              <div>
                <h2>{viewModel.document.customerName}</h2>
                <p class="muted">{viewModel.document.subtitle}</p>
              </div>
            </header>
            <dl class="document-facts document-facts--account">
              {viewModel.document.facts.map((fact) => (
                <div key={fact.label}>
                  <dt>{fact.label}</dt>
                  <dd>
                    {fact.valueKind === "money" ? formatMoney(fact.value as number) : fact.value}
                  </dd>
                </div>
              ))}
            </dl>
          </section>

          {viewModel.noPaymentDue && (
            <div class="paid-state" role="status">
              <strong>No payment due</strong>
              <span>Your account balance is paid in full. Thank you for your business.</span>
            </div>
          )}
        </div>
        <aside class="checkout-rail" aria-label="Payment summary">
          <div class="payment-due-card">
            <span>{viewModel.paymentRail.balanceLabel}</span>
            {viewModel.paymentRail.balanceValueKind === "paid" ? (
              <strong>Paid in full</strong>
            ) : (
              <strong>{formatMoney(viewModel.paymentRail.balanceValue)}</strong>
            )}
          </div>
          {viewModel.paymentRail.canPay && (
            <PaymentPanel
              resource={{
                scope: { type: "account", customerCode },
                ...viewModel.paymentPanel,
              }}
            />
          )}
        </aside>
      </div>
    </section>
  );
}
